import React, { useState, useEffect, useRef } from 'react';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// ── Design tokens ──
const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

// ── Management types ──
const MANAGEMENT_TYPES = [
  { id: 'cobranza', label: 'Cobranza', desc: 'Seguimiento de pagos y deudas pendientes' },
  { id: 'visita', label: 'Visita comercial', desc: 'Registro de visitas y acuerdos con el cliente' },
  { id: 'oportunidad', label: 'Oportunidad comercial', desc: 'Nuevas oportunidades de venta identificadas' },
  { id: 'competencia', label: 'Competencia o motivo de no compra', desc: 'Información sobre competidores o razones de pérdida' },
  { id: 'incidencia', label: 'Incidencia operativa', desc: 'Problemas operativos que requieren atención' },
];

const TYPE_LABELS = Object.fromEntries(MANAGEMENT_TYPES.map(t => [t.id, t.label]));
const URGENCY_LABELS = { baja: 'Baja', normal: 'Normal', alta: 'Alta', critica: 'Crítica' };
const URGENCY_COLORS = { baja: 'bg-gray-100 text-gray-600', normal: 'bg-blue-50 text-blue-700', alta: 'bg-amber-50 text-amber-700', critica: 'bg-red-50 text-red-700' };

// ── Shared UI primitives ──
const ProgressBar = ({ step }) => {
  const labels = ['Cliente', 'Tipo', 'Detalles', 'Revisar'];
  return (
    <div className="flex gap-1" data-testid="gc-progress">
      {labels.map((l, i) => (
        <div key={i} className="flex-1">
          <div className={`h-1 rounded-full transition-all duration-300 ${i + 1 <= step ? 'bg-[#C82432]' : 'bg-gray-200'}`} />
          <p className={`text-[10px] text-center mt-1 ${i + 1 === step ? 'text-[#C82432] font-semibold' : i + 1 < step ? 'text-gray-500' : 'text-gray-300'}`}>{l}</p>
        </div>
      ))}
    </div>
  );
};

const Field = ({ label, required, children, hint }) => (
  <div className="space-y-1">
    <label className="block text-xs font-medium text-gray-600">{label}{required && <span className="text-[#C82432] ml-0.5">*</span>}</label>
    {children}
    {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
  </div>
);

const SectionDivider = ({ title }) => (
  <div className="pt-3 pb-1 border-t border-gray-100">
    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{title}</p>
  </div>
);

// ── Initial form ──
const INITIAL_FORM = {
  management_date: new Date().toISOString().slice(0, 10),
  what_happened: '', next_step: '', follow_up_date: '',
  needs_support: false, support_area: '', urgency: 'normal',
  payment_situation: '', payment_promise: false, promise_amount: '', promise_date: '',
  perceived_risk: 'medio',
  contact_type: 'presencial', what_agreed: '', visit_result: '', estimated_repurchase_date: '',
  opportunity_type: '', product_interest: '', potential_volume: '', probability: 'media',
  main_reason: '', competitor_identified: false, competitor_name: '', competitor_brand: '',
  requested_product: '', loss_risk: 'medio',
  incident_type: '', responsible_team: '', client_impact: 'medio',
  what_needed: '',
};

const RESULT_OPTIONS = [
  { id: '', label: 'Seleccionar...' },
  { id: 'exitoso', label: 'Exitoso' },
  { id: 'sin_respuesta', label: 'Sin respuesta' },
  { id: 'pendiente', label: 'Pendiente' },
  { id: 'reprogramar', label: 'Reprogramar' },
  { id: 'no_aplica', label: 'No aplica' },
];

const SUPPORT_AREAS = ['Ventas', 'Cobranzas', 'Finanzas', 'Operaciones', 'Logistica', 'Gerencia'];

const ROLE_LABELS = {
  admin: 'Administrador', vendedora: 'Vendedor / Comercial',
  aprobador: 'Aprobador', logistica: 'Logística', chofer: 'Chofer',
};

const draftKey = (user) => `venege_gc_draft_${user?.id || user?.email || 'anon'}`;

// ════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════
export const GestionClienteModal = ({ isOpen, onClose, user, preSelectedClient }) => {
  const [step, setStep] = useState(1);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [customerNotFound, setCustomerNotFound] = useState(false);
  const [manualCustomerName, setManualCustomerName] = useState('');
  const [managementType, setManagementType] = useState('');
  const [formData, setFormData] = useState({ ...INITIAL_FORM });
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // {record, clickup}
  const [saveError, setSaveError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [storedDraft, setStoredDraft] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryMsg, setRetryMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const searchRef = useRef(null);

  // ── Load contacts ──
  useEffect(() => {
    if (isOpen && contacts.length === 0) loadContacts();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && preSelectedClient && !selectedContact) selectContact(preSelectedClient);
  }, [isOpen, preSelectedClient]);

  useEffect(() => {
    if (!isOpen) {
      setStep(1); setSelectedContact(null); setClientDetail(null); setSearchQuery('');
      setManagementType(''); setFormData({ ...INITIAL_FORM }); setIsDirty(false);
      setSaveResult(null); setSaveError(''); setCustomerNotFound(false);
      setManualCustomerName(''); setShowHistory(false); setStoredDraft(null); setRetryMsg('');
    }
  }, [isOpen]);

  // ── Local draft persistence (Fase 7) ──
  useEffect(() => {
    if (!isOpen) return;
    try {
      const raw = window.localStorage.getItem(draftKey(user));
      if (raw) setStoredDraft(JSON.parse(raw));
    } catch (e) { /* ignore corrupt draft */ }
  }, [isOpen, user]);

  useEffect(() => {
    if (!isOpen || !isDirty || saveResult) return;
    const timer = setTimeout(() => {
      const payload = {
        savedAt: new Date().toISOString(), step, managementType, formData,
        customer: selectedContact ? { contact_id: selectedContact.contact_id, contact_name: selectedContact.contact_name, company_name: selectedContact.company_name } : null,
        manualCustomerName, customerNotFound,
      };
      try { window.localStorage.setItem(draftKey(user), JSON.stringify(payload)); } catch (e) { /* quota */ }
    }, 500);
    return () => clearTimeout(timer);
  }, [isOpen, isDirty, saveResult, step, managementType, formData, selectedContact, manualCustomerName, customerNotFound, user]);

  const clearDraft = () => {
    try { window.localStorage.removeItem(draftKey(user)); } catch (e) { /* ignore */ }
    setStoredDraft(null);
  };

  const resumeDraft = () => {
    if (!storedDraft) return;
    setFormData({ ...INITIAL_FORM, ...(storedDraft.formData || {}) });
    setManagementType(storedDraft.managementType || '');
    setManualCustomerName(storedDraft.manualCustomerName || '');
    setCustomerNotFound(!!storedDraft.customerNotFound);
    if (storedDraft.customer) {
      setSelectedContact(storedDraft.customer);
      setSearchQuery(storedDraft.customer.contact_name || '');
      loadClientDetail(storedDraft.customer.contact_id);
    }
    setStep(storedDraft.step || 1);
    setIsDirty(true);
    setStoredDraft(null);
  };

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      let all = [], page = 1, hasMore = true;
      while (hasMore && page <= 30) {
        const params = new URLSearchParams({ page, per_page: 100, user_role: user?.role || '', user_name: user?.name || '' });
        const resp = await fetch(`${backendUrl}/api/zoho/contacts?${params.toString()}`);
        if (!resp.ok) break;
        const data = await resp.json();
        const pg = data?.data?.contacts || [];
        if (!pg.length) break;
        all = [...all, ...pg];
        hasMore = (data?.data?.page_context?.has_more_page || false) && pg.length === 100;
        page++;
      }
      setContacts(all);
    } catch (e) { console.error('Error loading contacts:', e); }
    finally { setLoadingContacts(false); }
  };

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery || searchQuery.length < 2) return false;
    const q = searchQuery.toUpperCase();
    return (c.contact_name || '').toUpperCase().includes(q) || (c.contact_id || '').toUpperCase().includes(q);
  }).slice(0, 15);

  const loadClientDetail = async (contactId) => {
    if (!contactId) return;
    setLoadingDetail(true); setClientDetail(null);
    try {
      const resp = await fetch(`${backendUrl}/api/clients/${contactId}/detail`);
      const data = await resp.json();
      if (data.success) setClientDetail(data);
    } catch (e) { console.error(e); }
    finally { setLoadingDetail(false); }
  };

  const selectContact = async (contact) => {
    setSelectedContact(contact); setSearchQuery(contact.contact_name);
    setShowDropdown(false); setCustomerNotFound(false); setManualCustomerName('');
    await loadClientDetail(contact.contact_id);
  };

  const handleChangeCustomer = () => {
    setSelectedContact(null); setClientDetail(null); setCustomerNotFound(false);
    setManualCustomerName(''); setSearchQuery(''); setStep(1);
    setTimeout(() => searchRef.current?.focus(), 100);
  };

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const handleClose = () => {
    if (isDirty && !saveResult) setShowExitConfirm(true);
    else onClose();
  };

  // ── Draft (local, per authenticated user) ──
  const saveDraft = () => {
    const payload = {
      savedAt: new Date().toISOString(), step, managementType, formData,
      customer: selectedContact ? { contact_id: selectedContact.contact_id, contact_name: selectedContact.contact_name, company_name: selectedContact.company_name } : null,
      manualCustomerName, customerNotFound,
    };
    try { window.localStorage.setItem(draftKey(user), JSON.stringify(payload)); } catch (e) { /* quota */ }
    setIsDirty(false);
  };

  // ── Save ──
  const buildPayload = (status) => ({
    customer_name: selectedContact?.contact_name || manualCustomerName || searchQuery,
    customer_id: selectedContact?.contact_id || null,
    customer_verified: !!selectedContact,
    management_type: managementType,
    management_date: formData.management_date,
    follow_up_owner: user?.name || '',
    what_happened: formData.what_happened,
    next_step: formData.next_step,
    follow_up_date: formData.follow_up_date,
    needs_support: formData.needs_support,
    support_area: formData.support_area,
    urgency: formData.urgency,
    type_specific_data: formData,
    status,
  });

  const validate = () => {
    const name = selectedContact?.contact_name || manualCustomerName;
    if (!name) return 'Selecciona un cliente válido para continuar.';
    if (!managementType) return 'Selecciona el tipo de gestión para continuar.';
    if (!formData.what_happened?.trim()) return 'Describe qué ocurrió para continuar.';
    if (formData.next_step?.trim() && !formData.follow_up_date) {
      return 'Define una próxima acción para mantener la continuidad de la gestión.';
    }
    if (formData.needs_support && !formData.support_area) return 'Indica el área responsable del apoyo.';
    return '';
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) { setSaveError(problem); return; }
    setSaving(true); setSaveError('');
    try {
      const resp = await fetch(`${backendUrl}/api/gestion-cliente`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(buildPayload('completed')),
      });
      const data = await resp.json();
      if (data.success || resp.ok) {
        setSaveResult({ record: data.data, clickup: data.clickup || {}, status: data.status || {} });
        setIsDirty(false);
        if (data.status?.clickup === 'synced') clearDraft();
      } else {
        setSaveError('Error al guardar. Intente nuevamente.');
      }
    } catch (e) { setSaveError('Error de conexión. Intente nuevamente.'); }
    finally { setSaving(false); }
  };

  const handleRetrySync = async () => {
    const id = saveResult?.record?.id;
    if (!id) return;
    setRetrying(true); setRetryMsg('');
    try {
      const resp = await fetch(`${backendUrl}/api/gestion-cliente/${id}/retry-sync`, {
        method: 'POST', credentials: 'include',
      });
      const data = await resp.json();
      if (data.success) {
        setSaveResult(prev => ({ ...prev,
          clickup: { ...prev.clickup, synced: true, task_url: data.task_url, list_name: data.list_name, partial: data.partial },
          status: { ...prev.status, clickup: 'synced', cliq: data.cliq || prev.status?.cliq } }));
        clearDraft();
        setRetryMsg('Sincronización completada.');
      } else {
        setRetryMsg(data.message || 'No se pudo sincronizar. Intenta nuevamente.');
      }
    } catch (e) { setRetryMsg('No se pudo sincronizar. Intenta nuevamente.'); }
    finally { setRetrying(false); }
  };

  // ── History ──
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const params = selectedContact?.contact_id ? `?customer_id=${selectedContact.contact_id}&limit=10` : '?limit=10';
      const resp = await fetch(`${backendUrl}/api/gestion-cliente/history${params}`, { credentials: 'include' });
      const data = await resp.json();
      if (data.success) setHistory(data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingHistory(false); }
  };

  const canAdvance = () => {
    if (step === 1) return !!(selectedContact || (customerNotFound && manualCustomerName));
    if (step === 2) return !!managementType;
    if (step === 3) {
      if (!formData.what_happened?.trim()) return false;
      if (formData.next_step?.trim() && !formData.follow_up_date) return false;
      if (formData.needs_support && !formData.support_area) return false;
      return true;
    }
    return true;
  };

  if (!isOpen) return null;

  // ════════ SUCCESS STATE ════════
  if (saveResult) {
    const r = saveResult.record || {};
    const cu = saveResult.clickup || {};
    const st = saveResult.status || {};
    const CLICKUP_STATE_LABEL = {
      synced: 'Sincronizado', not_linked: 'Sincronizado — sin vincular cliente',
      pending: 'Pendiente', failed: 'Pendiente de reintento',
      blocked: 'Bloqueado — revisión manual', not_applicable: 'No aplica',
    };
    const CLIQ_STATE_LABEL = {
      sent: 'Enviada', pending: 'Pendiente', failed: 'Falló',
      dry_run: 'Modo prueba (no enviada)', not_routed: 'Sin región asignada (no enviada)',
      not_applicable: 'No aplica',
    };
    const tone = (v, good) => v === good ? 'text-emerald-600'
      : (['blocked', 'failed', 'not_routed'].includes(v) ? 'text-red-600' : 'text-amber-600');
    return (
      <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
        <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="gc-success">
          {/* Success header */}
          <div className="bg-gradient-to-b from-emerald-50 to-white px-6 pt-8 pb-4 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900">Gestión guardada</h2>
            <p className="text-xs text-gray-400 mt-1">El registro ya está en el historial</p>
          </div>
          {/* Estado operativo real */}
          <div className="px-6 py-3 space-y-1.5 bg-gray-50/70 border-t border-gray-100" data-testid="gc-operational-status">
            <div className="flex justify-between text-xs"><span className="text-gray-500">Guardado</span><span className="font-semibold text-emerald-600" data-testid="gc-state-saved">Sí</span></div>
            <div className="flex justify-between text-xs"><span className="text-gray-500">ClickUp</span><span className={`font-semibold ${tone(st.clickup, 'synced')}`} data-testid="gc-state-clickup">{CLICKUP_STATE_LABEL[st.clickup] || 'Pendiente'}</span></div>
            <div className="flex justify-between text-xs"><span className="text-gray-500">Notificación Cliq</span><span className={`font-semibold ${tone(st.cliq, 'sent')}`} data-testid="gc-state-cliq">{CLIQ_STATE_LABEL[st.cliq] || 'Pendiente'}{st.cliq === 'sent' && st.cliq_exception ? ' — con aviso' : ''}</span></div>
            {st.region && <div className="flex justify-between text-xs"><span className="text-gray-500">Región del cliente</span><span className="font-semibold text-gray-700" data-testid="gc-state-region">{st.region === 'caracas' ? 'Caracas' : 'Oriente'}</span></div>}
            {(st.cliq_warnings || []).map((w, i) => (
              <p key={i} className="text-[10px] text-amber-600" data-testid={`gc-state-warning-${i}`}>{w}</p>
            ))}
            {cu.partial && <p className="text-[10px] text-amber-600">Sincronización financiera pendiente de validación.</p>}
          </div>
          {/* Summary */}
          <div className="px-6 py-4 space-y-2 border-t border-gray-100">
            <div className="flex justify-between text-sm"><span className="text-gray-400">Cliente</span><span className="font-medium text-gray-900 text-right truncate ml-4">{r.customer_name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Tipo</span><span className="font-medium text-gray-900">{TYPE_LABELS[r.management_type] || r.management_type}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-400">Registrado por</span><span className="font-medium text-gray-900">{r.created_by_name || user?.name}</span></div>
            {r.next_step && <div className="flex justify-between text-sm"><span className="text-gray-400">Próximo paso</span><span className="font-medium text-gray-900 text-right truncate ml-4">{r.next_step}</span></div>}
            {r.follow_up_date && <div className="flex justify-between text-sm"><span className="text-gray-400">Seguimiento</span><span className="font-medium text-gray-900">{r.follow_up_date}</span></div>}
            {cu.list_name && <div className="flex justify-between text-sm"><span className="text-gray-400">Destino</span><span className="font-medium text-gray-900">{cu.list_name}</span></div>}
            <div className="flex justify-between text-sm"><span className="text-gray-400">Tarea pendiente</span><span className="font-medium text-gray-900">{r.activity_status === 'open' ? 'Sí, con seguimiento' : 'No, gestión cerrada'}</span></div>
          </div>
          {/* Actions */}
          <div className="px-6 pb-6 pt-2 space-y-2">
            {st.clickup && !['synced', 'not_linked', 'blocked'].includes(st.clickup) && (
              <button onClick={handleRetrySync} disabled={retrying} data-testid="gc-retry-sync"
                className="w-full py-2.5 border border-amber-300 bg-amber-50 text-amber-700 rounded-xl text-xs font-semibold hover:bg-amber-100 disabled:opacity-50 transition-colors">
                {retrying ? 'Reintentando...' : 'Reintentar sincronización'}
              </button>
            )}
            {st.clickup === 'not_linked' && (
              <p className="text-[11px] text-center text-amber-600" data-testid="gc-not-linked-note">
                La tarea se creó en ClickUp pero el cliente no pudo verificarse: quedó marcada para revisión.
              </p>
            )}
            {st.clickup === 'blocked' && (
              <p className="text-[11px] text-center text-red-600" data-testid="gc-blocked-note">
                El cliente no pudo identificarse en ClickUp. Un administrador debe revisarlo; la gestión quedó guardada.
              </p>
            )}
            {retryMsg && <p className="text-[11px] text-center text-gray-500" data-testid="gc-retry-msg">{retryMsg}</p>}
            <button onClick={() => { clearDraft(); onClose(); }} data-testid="gc-success-close" className="w-full py-3 bg-[#C82432] text-white rounded-xl text-sm font-semibold hover:bg-[#A51D28] active:scale-[0.98] transition-all">Cerrar</button>
            <div className="flex gap-2">
              <button onClick={() => { clearDraft(); setSaveResult(null); setStep(1); setManagementType(''); setFormData({...INITIAL_FORM}); handleChangeCustomer(); }} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:bg-gray-50 transition-colors" data-testid="gc-new-record">Nueva gestión</button>
              <button onClick={() => { setSaveResult(null); setShowHistory(true); loadHistory(); }} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-medium hover:bg-gray-50 transition-colors" data-testid="gc-view-history">Ver historial</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ════════ HISTORY VIEW ════════
  if (showHistory) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={handleClose}>
        <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh]" onClick={e => e.stopPropagation()} data-testid="gc-history">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-lg font-bold text-gray-900">Historial reciente</h2>
            <div className="flex gap-2">
              <button onClick={() => { setShowHistory(false); setStep(1); }} className="text-xs text-[#C82432] font-medium hover:underline">Volver</button>
              <button onClick={handleClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-5 h-5 border-2 border-[#C82432] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-12">No hay registros recientes</p>
            ) : (
              <div className="space-y-3">
                {history.map(h => (
                  <div key={h.id} className="border border-gray-100 rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900">{h.customer_name}</span>
                      <span className="text-[10px] text-gray-400">{h.management_date || h.created_at?.slice(0, 10)}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 bg-red-50 text-[#C82432] text-[10px] font-semibold rounded-full">{TYPE_LABELS[h.management_type] || h.management_type}</span>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${URGENCY_COLORS[h.urgency] || ''}`}>{URGENCY_LABELS[h.urgency] || h.urgency}</span>
                      {h.clickup_sync_status === 'synced' && !h.clickup_partial && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-semibold rounded-full">Sincronizado</span>}
                      {h.clickup_sync_status === 'synced' && h.clickup_partial && <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-semibold rounded-full">Sincronización parcial</span>}
                      {h.clickup_sync_status === 'sync_pending' && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-semibold rounded-full">Pendiente</span>}
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${h.activity_status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'}`}>{h.activity_status === 'closed' ? 'Cerrada' : 'Abierta'}</span>
                    </div>
                    {h.next_step && <p className="text-xs text-gray-500 truncate">Próximo: {h.next_step}</p>}
                    {h.clickup_list_name && <p className="text-[10px] text-gray-400">Destino: {h.clickup_list_name}</p>}
                    {h.created_by_name && <p className="text-[10px] text-gray-400">Por: {h.created_by_name}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ════════ EXIT CONFIRM ════════
  const ExitConfirmDialog = () => (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="gc-exit-confirm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4">
        <h3 className="text-base font-bold text-gray-900">Cambios sin guardar</h3>
        <p className="text-sm text-gray-500">Si sales ahora, perderás los datos ingresados.</p>
        <div className="space-y-2">
          <button onClick={() => setShowExitConfirm(false)} className="w-full py-2.5 bg-[#C82432] text-white rounded-xl text-sm font-semibold hover:bg-[#A51D28] transition-colors" data-testid="gc-exit-continue">Seguir editando</button>
          <button onClick={() => { saveDraft(); setShowExitConfirm(false); onClose(); }} className="w-full py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors" data-testid="gc-exit-draft">Guardar borrador</button>
          <button onClick={() => { setShowExitConfirm(false); onClose(); }} className="w-full py-2 text-gray-400 text-xs hover:text-gray-600 transition-colors" data-testid="gc-exit-discard">Salir sin guardar</button>
        </div>
      </div>
    </div>
  );

  // ── Customer summary ──
  const CustomerSummary = ({ compact }) => {
    const name = selectedContact?.contact_name || manualCustomerName;
    if (!name) return null;
    return (
      <div className={`rounded-xl border ${customerNotFound ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100 bg-gray-50/50'} ${compact ? 'p-3' : 'p-4'}`} data-testid="gc-customer-summary">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className={`font-semibold text-gray-900 truncate ${compact ? 'text-sm' : ''}`}>{name}</p>
            {selectedContact?.company_name && selectedContact.company_name !== selectedContact.contact_name && <p className="text-[11px] text-gray-500 truncate">{selectedContact.company_name}</p>}
            {customerNotFound && <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full">Pendiente verificación</span>}
          </div>
          {step <= 2 && <button onClick={handleChangeCustomer} className="text-xs text-[#C82432] hover:underline whitespace-nowrap" data-testid="gc-change-customer">Cambiar</button>}
        </div>
        {!compact && (
          <>
          <p className="text-[9px] text-gray-400 mt-3 uppercase tracking-wide">Información financiera sincronizada desde Zoho</p>
          {loadingDetail ? (
            <p className="text-[10px] text-gray-400 mt-1">Consultando Zoho...</p>
          ) : clientDetail ? (
          <>
          <div className="grid grid-cols-3 gap-1.5 mt-1.5">
            <div className="text-center p-1.5 bg-white rounded-lg border border-gray-100">
              <p className="text-[9px] text-gray-400 uppercase">Deuda total</p>
              <p className="text-xs font-bold text-gray-900">{formatCurrency(clientDetail.total_debt)}</p>
            </div>
            <div className="text-center p-1.5 bg-white rounded-lg border border-gray-100">
              <p className="text-[9px] text-gray-400 uppercase">Vigente</p>
              <p className="text-xs font-bold text-emerald-700">{formatCurrency(clientDetail.vigente)}</p>
            </div>
            <div className="text-center p-1.5 bg-white rounded-lg border border-gray-100">
              <p className="text-[9px] text-gray-400 uppercase">Vencido</p>
              <p className="text-xs font-bold text-red-600">{formatCurrency((clientDetail.vencido_0_7||0)+(clientDetail.vencido_7_15||0)+(clientDetail.vencido_15_plus||0))}</p>
            </div>
          </div>
          <p className="text-[9px] text-gray-400 mt-1.5">Solo lectura. Se envía automáticamente a ClickUp.</p>
          </>
          ) : (
            <p className="text-[10px] text-gray-400 mt-1" data-testid="gc-no-financial">No hay información financiera disponible en Zoho.</p>
          )}
          </>
        )}
      </div>
    );
  };

  // ── Identidad del registrador (no editable) ──
  const RegisteringAs = () => (
    <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3" data-testid="gc-registering-as">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Registrando como</p>
      <p className="text-sm font-semibold text-gray-900 mt-0.5">{user?.name || 'Usuario'}</p>
      <p className="text-[11px] text-gray-500">{ROLE_LABELS[user?.role] || user?.role || ''}</p>
    </div>
  );

  const DraftBanner = () => {
    if (!storedDraft) return null;
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="gc-draft-banner">
        <div>
          <p className="text-xs font-semibold text-amber-800">Borrador pendiente</p>
          <p className="text-[11px] text-amber-700">
            {storedDraft.customer?.contact_name || storedDraft.manualCustomerName || 'Sin cliente'} · guardado {String(storedDraft.savedAt || '').slice(0, 16).replace('T', ' ')}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={resumeDraft} className="flex-1 py-2 bg-[#C82432] text-white rounded-lg text-xs font-semibold hover:bg-[#A51D28] transition-colors" data-testid="gc-resume-draft">Continuar borrador</button>
          <button onClick={clearDraft} className="flex-1 py-2 border border-amber-300 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors" data-testid="gc-discard-draft">Descartar borrador</button>
        </div>
      </div>
    );
  };

  // ═════════ STEP 1 ═════════
  const Step1 = () => (
    <div className="space-y-4" data-testid="gc-step-1">
      {DraftBanner()}
      {RegisteringAs()}
      {selectedContact || customerNotFound ? CustomerSummary({}) : (
        <>
          <div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true); }}
                onFocus={() => searchQuery.length >= 2 && setShowDropdown(true)}
                placeholder="Buscar por nombre o ID de cliente..."
                className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-xl bg-white focus:border-[#C82432] focus:ring-1 focus:ring-[#C82432]/20 outline-none transition-colors"
                data-testid="gc-customer-search"
                autoFocus
              />
            </div>
            {showDropdown && searchQuery.length >= 2 && (
              <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                {loadingContacts ? (
                  <div className="p-4 text-center text-sm text-gray-400">Cargando clientes...</div>
                ) : filteredContacts.length > 0 ? (
                  filteredContacts.map(c => (
                    <button key={c.contact_id} onClick={() => selectContact(c)} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors" data-testid={`gc-contact-${c.contact_id}`}>
                      <p className="text-sm font-medium text-gray-900 truncate">{c.contact_name}</p>
                      {c.company_name && <p className="text-[11px] text-gray-500 truncate">{c.company_name}</p>}
                    </button>
                  ))
                ) : (
                  <div className="p-4 space-y-3">
                    <p className="text-sm font-medium text-gray-700 text-center">Cliente no encontrado en Zoho.</p>
                    <p className="text-[11px] text-gray-400 text-center">Verifica el nombre o el identificador. Puedes continuar y enviarlo a cartera para seguimiento.</p>
                    <button onClick={() => { setCustomerNotFound(true); setManualCustomerName(searchQuery); setShowDropdown(false); }} className="w-full py-2.5 border border-amber-300 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-100 transition-colors" data-testid="gc-continue-not-found">Continuar sin verificar</button>
                  </div>
                )}
              </div>
            )}
          </div>
          {loadingDetail && <div className="flex items-center gap-2 py-3"><div className="w-4 h-4 border-2 border-[#C82432] border-t-transparent rounded-full animate-spin" /><span className="text-sm text-gray-500">Cargando...</span></div>}
        </>
      )}
    </div>
  );

  // ═════════ STEP 2 ═════════
  const Step2 = () => (
    <div className="space-y-3" data-testid="gc-step-2">
      {CustomerSummary({ compact: true })}
      <div className="space-y-2">
        {MANAGEMENT_TYPES.map(t => (
          <button key={t.id} onClick={() => { setManagementType(t.id); setIsDirty(true); }} data-testid={`gc-type-${t.id}`}
            className={`w-full flex items-start gap-3 p-3.5 rounded-xl border text-left transition-all ${managementType === t.id ? 'border-[#C82432] bg-red-50/50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${managementType === t.id ? 'text-[#C82432]' : 'text-gray-900'}`}>{t.label}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{t.desc}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // ═════════ STEP 3 ═════════
  const Step3 = () => {
    const inputClass = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:border-[#C82432] focus:ring-1 focus:ring-[#C82432]/20 outline-none transition-colors";
    const selectClass = inputClass;
    const textareaClass = inputClass + " resize-none";
    const chipClass = (active) => `px-4 py-2 rounded-lg text-sm font-medium border transition-all ${active ? 'border-[#C82432] bg-red-50 text-[#C82432]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`;

    return (
      <div className="space-y-4" data-testid="gc-step-3">
        <div className="flex items-center gap-2 flex-wrap">
          {CustomerSummary({ compact: true })}
          <div className="flex items-center gap-2 mt-1 w-full">
            <span className="px-2.5 py-1 bg-red-50 text-[#C82432] text-[11px] font-semibold rounded-full">{TYPE_LABELS[managementType]}</span>
            <button onClick={() => setStep(2)} className="text-[11px] text-gray-400 hover:text-[#C82432]">Cambiar</button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Fecha de la gestión" required>
            <input type="date" value={formData.management_date} onChange={e => updateField('management_date', e.target.value)} className={inputClass} data-testid="gc-date" />
          </Field>
          <Field label="Urgencia">
            <select value={formData.urgency} onChange={e => updateField('urgency', e.target.value)} className={selectClass} data-testid="gc-urgency">
              <option value="baja">Baja</option><option value="normal">Normal</option><option value="alta">Alta</option><option value="critica">Crítica</option>
            </select>
          </Field>
        </div>
        <Field label="Qué ocurrió" required>
          <textarea rows={3} value={formData.what_happened} onChange={e => updateField('what_happened', e.target.value)} placeholder="Describe brevemente..." className={textareaClass} data-testid="gc-what-happened" />
        </Field>
        <Field label="Resultado">
          <select value={formData.visit_result} onChange={e => updateField('visit_result', e.target.value)} className={selectClass} data-testid="gc-result">
            {RESULT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Próxima acción" hint="Déjalo vacío si la gestión queda cerrada sin seguimiento.">
          <textarea rows={2} value={formData.next_step} onChange={e => updateField('next_step', e.target.value)} placeholder="Siguiente acción..." className={textareaClass} data-testid="gc-next-step" />
        </Field>
        {formData.next_step?.trim() && (
          <Field label="Fecha de próxima acción" required hint={!formData.follow_up_date ? 'Define una próxima acción para mantener la continuidad de la gestión.' : ''}>
            <input type="date" value={formData.follow_up_date} onChange={e => updateField('follow_up_date', e.target.value)} className={inputClass} data-testid="gc-followup-date" />
          </Field>
        )}
        <Field label="Requiere apoyo de otra área">
          <div className="flex gap-2">
            <button type="button" className={chipClass(formData.needs_support)} onClick={() => updateField('needs_support', true)} data-testid="gc-support-yes">Sí</button>
            <button type="button" className={chipClass(!formData.needs_support)} onClick={() => updateField('needs_support', false)} data-testid="gc-support-no">No</button>
          </div>
        </Field>
        {formData.needs_support && (
          <Field label="Área responsable" required>
            <select value={formData.support_area} onChange={e => updateField('support_area', e.target.value)} className={selectClass} data-testid="gc-support-area">
              <option value="">Seleccionar...</option>
              {SUPPORT_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
        )}

        {/* Cobranza */}
        {managementType === 'cobranza' && (<>
          <SectionDivider title="Cobranza" />
          <Field label="Situación actual del pago"><textarea rows={2} value={formData.payment_situation} onChange={e => updateField('payment_situation', e.target.value)} className={textareaClass} data-testid="gc-payment-situation" /></Field>
          <Field label="Promesa de pago"><div className="flex gap-2"><button type="button" className={chipClass(formData.payment_promise)} onClick={() => updateField('payment_promise', true)} data-testid="gc-promise-yes">Sí</button><button type="button" className={chipClass(!formData.payment_promise)} onClick={() => updateField('payment_promise', false)} data-testid="gc-promise-no">No</button></div></Field>
          {formData.payment_promise && <div className="grid grid-cols-2 gap-3"><Field label="Monto prometido"><input type="number" value={formData.promise_amount} onChange={e => updateField('promise_amount', e.target.value)} className={inputClass} data-testid="gc-promise-amount" /></Field><Field label="Fecha prometida"><input type="date" value={formData.promise_date} onChange={e => updateField('promise_date', e.target.value)} className={inputClass} data-testid="gc-promise-date" /></Field></div>}
          <Field label="Riesgo percibido"><select value={formData.perceived_risk} onChange={e => updateField('perceived_risk', e.target.value)} className={selectClass} data-testid="gc-risk"><option value="bajo">Bajo</option><option value="medio">Medio</option><option value="alto">Alto</option><option value="critico">Crítico</option></select></Field>
        </>)}

        {/* Visita */}
        {managementType === 'visita' && (<>
          <SectionDivider title="Visita comercial" />
          <Field label="Tipo de contacto"><select value={formData.contact_type} onChange={e => updateField('contact_type', e.target.value)} className={selectClass} data-testid="gc-contact-type"><option value="presencial">Presencial</option><option value="telefonica">Telefónica</option><option value="virtual">Virtual</option><option value="whatsapp">WhatsApp</option></select></Field>
          <Field label="Qué se acordó"><textarea rows={2} value={formData.what_agreed} onChange={e => updateField('what_agreed', e.target.value)} className={textareaClass} data-testid="gc-agreed" /></Field>
          <Field label="Fecha estimada recompra"><input type="date" value={formData.estimated_repurchase_date} onChange={e => updateField('estimated_repurchase_date', e.target.value)} className={inputClass} data-testid="gc-repurchase-date" /></Field>
        </>)}

        {/* Oportunidad */}
        {managementType === 'oportunidad' && (<>
          <SectionDivider title="Oportunidad comercial" />
          <Field label="Tipo de oportunidad"><input value={formData.opportunity_type} onChange={e => updateField('opportunity_type', e.target.value)} placeholder="Venta cruzada, nuevo producto..." className={inputClass} data-testid="gc-opp-type" /></Field>
          <Field label="Producto / línea de interés"><input value={formData.product_interest} onChange={e => updateField('product_interest', e.target.value)} className={inputClass} data-testid="gc-product" /></Field>
          <div className="grid grid-cols-2 gap-3"><Field label="Volumen potencial"><input value={formData.potential_volume} onChange={e => updateField('potential_volume', e.target.value)} className={inputClass} data-testid="gc-volume" /></Field><Field label="Probabilidad"><select value={formData.probability} onChange={e => updateField('probability', e.target.value)} className={selectClass} data-testid="gc-probability"><option value="baja">Baja</option><option value="media">Media</option><option value="alta">Alta</option></select></Field></div>
        </>)}

        {/* Competencia */}
        {managementType === 'competencia' && (<>
          <SectionDivider title="Competencia / No compra" />
          <Field label="Motivo principal"><textarea rows={2} value={formData.main_reason} onChange={e => updateField('main_reason', e.target.value)} className={textareaClass} data-testid="gc-main-reason" /></Field>
          <Field label="Competidor identificado"><div className="flex gap-2"><button type="button" className={chipClass(formData.competitor_identified)} onClick={() => updateField('competitor_identified', true)} data-testid="gc-comp-yes">Sí</button><button type="button" className={chipClass(!formData.competitor_identified)} onClick={() => updateField('competitor_identified', false)} data-testid="gc-comp-no">No</button></div></Field>
          {formData.competitor_identified && <div className="grid grid-cols-2 gap-3"><Field label="Competidor"><input value={formData.competitor_name} onChange={e => updateField('competitor_name', e.target.value)} className={inputClass} data-testid="gc-comp-name" /></Field><Field label="Marca/línea"><input value={formData.competitor_brand} onChange={e => updateField('competitor_brand', e.target.value)} className={inputClass} data-testid="gc-comp-brand" /></Field></div>}
          <Field label="Producto solicitado"><input value={formData.requested_product} onChange={e => updateField('requested_product', e.target.value)} className={inputClass} data-testid="gc-requested-product" /></Field>
          <Field label="Riesgo de pérdida"><select value={formData.loss_risk} onChange={e => updateField('loss_risk', e.target.value)} className={selectClass} data-testid="gc-loss-risk"><option value="bajo">Bajo</option><option value="medio">Medio</option><option value="alto">Alto</option></select></Field>
        </>)}

        {/* Incidencia */}
        {managementType === 'incidencia' && (<>
          <SectionDivider title="Incidencia operativa" />
          <Field label="Tipo de incidencia"><select value={formData.incident_type} onChange={e => updateField('incident_type', e.target.value)} className={selectClass} data-testid="gc-incident-type"><option value="">Seleccionar...</option><option value="entrega">Entrega</option><option value="facturacion">Facturación</option><option value="producto">Producto</option><option value="servicio">Servicio</option><option value="otro">Otro</option></select></Field>
          <Field label="Equipo responsable"><input value={formData.responsible_team} onChange={e => updateField('responsible_team', e.target.value)} className={inputClass} data-testid="gc-resp-team" /></Field>
          <Field label="Impacto"><select value={formData.client_impact} onChange={e => updateField('client_impact', e.target.value)} className={selectClass} data-testid="gc-impact"><option value="bajo">Bajo</option><option value="medio">Medio</option><option value="alto">Alto</option><option value="critico">Crítico</option></select></Field>
          <Field label="Qué se necesita"><textarea rows={2} value={formData.what_needed} onChange={e => updateField('what_needed', e.target.value)} className={textareaClass} data-testid="gc-what-needed" /></Field>
        </>)}
      </div>
    );
  };

  // ═════════ STEP 4: REVIEW ═════════
  const Step4 = () => {
    const Row = ({ label, value, goStep }) => {
      if (!value) return null;
      return (
        <div className="flex items-start justify-between py-2 border-b border-gray-50 last:border-0">
          <div className="min-w-0 flex-1"><p className="text-[11px] text-gray-400">{label}</p><p className="text-sm text-gray-900 mt-0.5 break-words">{value}</p></div>
          {goStep && <button onClick={() => setStep(goStep)} className="text-[11px] text-[#C82432] hover:underline ml-2 flex-shrink-0">Editar</button>}
        </div>
      );
    };
    return (
      <div className="space-y-3" data-testid="gc-step-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Resumen de gestión</p>
          <Row label="Cliente" value={selectedContact?.contact_name || manualCustomerName} goStep={1} />
          {customerNotFound && <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full mb-1">Pendiente verificación</span>}
          <Row label="Tipo de gestión" value={TYPE_LABELS[managementType]} goStep={2} />
          <Row label="Fecha" value={formData.management_date} goStep={3} />
          <Row label="Registrado por" value={`${user?.name || ''}${user?.role ? ` · ${ROLE_LABELS[user.role] || user.role}` : ''}`} />
          <Row label="Qué ocurrió" value={formData.what_happened} goStep={3} />
          <Row label="Resultado" value={RESULT_OPTIONS.find(o => o.id === formData.visit_result)?.label} goStep={3} />
          <Row label="Próxima acción" value={formData.next_step || 'Sin próxima acción (gestión cerrada)'} goStep={3} />
          <Row label="Fecha de próxima acción" value={formData.follow_up_date} goStep={3} />
          <div className="flex items-start justify-between py-2">
            <div><p className="text-[11px] text-gray-400">Urgencia</p><span className={`inline-block mt-1 px-2 py-0.5 text-[11px] font-semibold rounded-full ${URGENCY_COLORS[formData.urgency]}`}>{URGENCY_LABELS[formData.urgency]}</span></div>
            <button onClick={() => setStep(3)} className="text-[11px] text-[#C82432] hover:underline">Editar</button>
          </div>
          {formData.needs_support && <Row label="Área de apoyo" value={formData.support_area} goStep={3} />}
          <div className="pt-2 mt-1 border-t border-gray-50">
            <p className="text-[10px] text-gray-400">
              {formData.next_step?.trim() && formData.follow_up_date
                ? 'Esta gestión quedará como tarea pendiente con seguimiento.'
                : 'Esta gestión se guardará como completada, sin tarea pendiente.'}
            </p>
          </div>
        </div>
        {saveError && <p className="text-sm text-red-600 text-center" data-testid="gc-save-error">{saveError}</p>}
      </div>
    );
  };

  // ═════════ RENDER ═════════
  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={handleClose} data-testid="gc-modal-overlay">
        <div onClick={e => e.stopPropagation()} className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh]" data-testid="gc-modal" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
            <div>
              <h2 className="text-base font-bold text-gray-900" data-testid="gc-title">Gestión Cliente</h2>
              <p className="text-[11px] text-gray-400">Paso {step} de 4</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setShowHistory(true); loadHistory(); }} className="text-[11px] text-gray-400 hover:text-[#C82432] transition-colors" data-testid="gc-open-history">Historial</button>
              <button onClick={handleClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="gc-close-btn">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
          </div>
          {/* Progress */}
          <div className="px-5 pt-2.5 pb-1 flex-shrink-0"><ProgressBar step={step} /></div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {step === 1 && Step1()}
            {step === 2 && Step2()}
            {step === 3 && Step3()}
            {step === 4 && Step4()}
          </div>
          {/* Footer */}
          <div className="px-5 py-3.5 border-t border-gray-100 flex-shrink-0 flex items-center gap-2" style={{ paddingBottom: `max(0.875rem, env(safe-area-inset-bottom, 0px))` }}>
            {step > 1 && <button onClick={() => setStep(s => s - 1)} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" data-testid="gc-back">Atrás</button>}
            <div className="flex-1" />
            {isDirty && step < 4 && <button onClick={saveDraft} className="px-3 py-2 text-[11px] text-gray-400 hover:text-gray-600 transition-colors" data-testid="gc-save-draft">Guardar borrador</button>}
            {step < 4 ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canAdvance()} className="px-5 py-3 min-h-[44px] bg-[#C82432] text-white text-sm font-semibold rounded-xl hover:bg-[#A51D28] disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all shadow-sm" data-testid="gc-continue">Continuar</button>
            ) : (
              <button onClick={handleSave} disabled={saving} className="px-5 py-3 min-h-[44px] bg-[#C82432] text-white text-sm font-semibold rounded-xl hover:bg-[#A51D28] disabled:opacity-50 active:scale-[0.98] transition-all shadow-sm" data-testid="gc-save">{saving ? 'Guardando...' : 'Guardar gestión'}</button>
            )}
          </div>
        </div>
      </div>
      {showExitConfirm && ExitConfirmDialog()}
    </>
  );
};
